import { EvaluationDto } from 'actions/evaluations/EvaluationsDtos';
import { convertToRoom } from 'actions/rooms/RoomsDataConverter';
import {
    BusinessAreasWithServicesDto,
    FixedPriceOptionDto,
    ServiceDto,
    VariableDurationDto,
    VariablePriceDto
} from 'actions/services/ServicesDtos';
import { store } from 'store';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { MasterData } from 'types/MasterData';
import {
    BusinessAreasWithServices,
    BoardingMetadata,
    DaycareMetadata,
    EvaluationMetadata,
    LATE_CHECKOUT_FEE_TYPES,
    LATE_PICKUP_FEE_TYPES,
    Service,
    ServiceFixedPrice,
    ServiceMetadataDuration,
    ServiceVariableDuration,
    ServiceVariablePrice
} from 'types/Service';
import { parseJSON } from 'utils/json';

export const convertToService = (serviceDto: ServiceDto): Service => {
    return {
        id: serviceDto.id,
        name: serviceDto.name,
        description: serviceDto.description,
        fixedPrice: convertFixedPrice(serviceDto),
        variablePrice: convertVariablePrice(serviceDto),
        variableDuration: convertVariableDuration(serviceDto),
        petList: serviceDto.pet_list,
        durationHours: serviceDto.duration_hours,
        durationMinutes: serviceDto.duration_minutes,
        staffIds: serviceDto.staff.map(item => item.id),
        businessArea: serviceDto.business_area as MarketplaceServiceCategory,

        daycareMetadata:
            serviceDto.business_area === MarketplaceServiceCategory.DAYCARE
                ? convertDaycareMetadata(serviceDto.variable_duration)
                : undefined,
        boardingMetadata:
            serviceDto.business_area === MarketplaceServiceCategory.BOARDING
                ? convertBoardingMetadata(serviceDto.variable_duration)
                : undefined,
        evaluationMetadata:
            serviceDto.business_area === MarketplaceServiceCategory.EVALUATION
                ? convertEvaluationMetadata(serviceDto.evaluation)
                : undefined,

        dropOffTime: {
            start: serviceDto.drop_off_start || 0,
            end: serviceDto.drop_off_end || 0
        },
        pickUpTime: {
            start: serviceDto.pick_up_start || 0,
            end: serviceDto.pick_up_end || 0
        },
        checkInTime: {
            start: serviceDto.check_in_start || 0,
            end: serviceDto.check_in_end || 0
        },
        checkOutTime: {
            start: serviceDto.check_out_start || 0,
            end: serviceDto.check_out_end || 0
        },

        rooms: serviceDto.rooms?.map(room => convertToRoom(room))
    };
};

const convertFixedPrice = (serviceDto: ServiceDto): Array<ServiceFixedPrice> => {
    const masterData: MasterData = store.getState().masterData.data;

    if (serviceDto.fixed_price === null) {
        return [];
    } else {
        const responseFixedPrice: Array<FixedPriceOptionDto> = JSON.parse(serviceDto.fixed_price);

        return responseFixedPrice.map(fp => ({
            petType: masterData.petTypes.find(pt => pt.id === fp.pet_type_id)!,
            price: fp.price
        }));
    }
};

const convertVariablePrice = (serviceDto: ServiceDto): ServiceVariablePrice | undefined => {
    const masterData = store.getState().masterData.data;
    const marketplace = store.getState().marketplace.data;

    if (serviceDto.variable_price === null) {
        return undefined;
    }

    if (serviceDto.variable_price !== null) {
        const responseVariablePrice: VariablePriceDto = JSON.parse(serviceDto.variable_price);

        return {
            variables: {
                petTypeVariable: responseVariablePrice.variables.pet_type_variable,
                petSizeVariable: responseVariablePrice.variables.pet_size_variable,
                hairTypeVariable: responseVariablePrice.variables.hair_type_variable,
                hairLengthVariable: responseVariablePrice.variables.hair_length_variable,
                exoticTypeVariable: responseVariablePrice.variables.exotic_type_variable
            },
            prices: responseVariablePrice.prices.map(vp => {
                const petType = masterData.petTypes.find(pt => pt.id === vp.pet_type_id);
                const petTypeData =
                    marketplace.petTypes[vp.pet_type_id as keyof typeof marketplace.petTypes];
                const petSize =
                    masterData.petSizes &&
                    masterData.petSizes
                        .map(s => s.element)
                        .find(s => s.id === vp.size_meta_variable);

                const hairLengthByPetType = petTypeData.hairLengths;

                const hairLengthOptions = hairLengthByPetType?.find(
                    hairLength => hairLength.id === vp.hair_length_meta_variable
                );

                return {
                    petType: petType,
                    price: vp.price,
                    petSize: petSize,
                    hairLength: hairLengthOptions
                };
            })
        };
    }
};

const convertVariableDuration = (
    serviceDto: ServiceDto
): Array<ServiceVariableDuration> | ServiceMetadataDuration => {
    const masterData: MasterData = store.getState().masterData.data;
    const petSizes = masterData.petSizes;
    const rawVariableDuration = serviceDto.variable_duration;

    if (rawVariableDuration === null || rawVariableDuration.length === 0) {
        return [];
    }

    if (
        [
            MarketplaceServiceCategory.DAYCARE,
            MarketplaceServiceCategory.BOARDING,
            MarketplaceServiceCategory.TRAINING,
            MarketplaceServiceCategory.WALKING,
            MarketplaceServiceCategory.TRANSPORTATION
        ].includes(serviceDto.business_area as MarketplaceServiceCategory)
    ) {
        return {
            metadata: rawVariableDuration
        } as ServiceMetadataDuration;
    }

    return parseJSON(rawVariableDuration, [])
        .map((vd: VariableDurationDto) => {
            const petSize = petSizes.find(ps => ps.element.id === vd.size_meta_variable);

            if (!petSize) {
                return undefined;
            }

            return {
                petSize: {
                    id: petSize?.element.id,
                    name: petSize?.element.name
                },
                duration: { hours: vd.hours, minutes: vd.minutes }
            };
        })
        .filter(Boolean) as ServiceVariableDuration[];
};

const convertDaycareMetadata = (metadataJson: string): DaycareMetadata | undefined => {
    try {
        const raw = JSON.parse(metadataJson);

        const durationValue = raw.duration_value || 'Up to 8 hours';
        const durationHours = parseInt(durationValue.replace(/\D/g, ''), 10) || 0;

        return {
            durationOption: raw.duration_option || 'full_day',
            durationValue: durationValue,
            durationHours: durationHours,
            latePickupFee: {
                enabled: raw.late_pickup_fee?.enabled || false,
                type: raw.late_pickup_fee?.type || LATE_PICKUP_FEE_TYPES.PER_HOUR,
                gracePeriod: raw.late_pickup_fee?.grace_period || 15,
                fees: raw.late_pickup_fee?.fees || {}
            },
            roomsEnabled: raw.rooms_enabled || false,
            selectedRooms: raw.selected_rooms.map((room: string) => parseInt(room)) || []
        };
    } catch (error) {
        return undefined;
    }
};

const convertBoardingMetadata = (metadataJson: string): BoardingMetadata | undefined => {
    try {
        const raw = JSON.parse(metadataJson);

        return {
            dropoff24h: raw.dropoff_24h || false,
            roomsEnabled: raw.rooms_enabled || false,
            selectedRooms: (raw.selected_rooms || []).map((room: string | number) =>
                typeof room === 'string' ? parseInt(room, 10) : room
            ),
            minNights: raw.min_nights || 1,
            latePickupFee: {
                enabled: raw.late_pickup_fee?.enabled || false,
                type: raw.late_pickup_fee?.type || LATE_CHECKOUT_FEE_TYPES.FIXED_FEE,
                gracePeriod: raw.late_pickup_fee?.grace_period || 30,
                fees: raw.late_pickup_fee?.fees || {}
            }
        };
    } catch (error) {
        return undefined;
    }
};

const convertEvaluationMetadata = (evaluation?: EvaluationDto): EvaluationMetadata | undefined => {
    return {
        businessAreas:
            evaluation?.category_list.map(category => category as MarketplaceServiceCategory) || [],
        expirationInMonths: evaluation?.evaluation_expiry || null
    };
};

export const convertToBusinessAreasWithServices = (
    businessAreasDto: BusinessAreasWithServicesDto
): BusinessAreasWithServices => {
    return {
        id: businessAreasDto.id,
        hasService: businessAreasDto.has_service,
        trainingAtFacility: businessAreasDto.training_at_facility,
        trainingInHome: businessAreasDto.training_in_home ?? false,
        walkingAtFacility: businessAreasDto.walking_at_facility,
        walkingInHome: businessAreasDto.walking_in_home ?? false
    };
};
